import { useSyncExternalStore } from "react";
// Platform gate for desktop-APP-only behavior: window chrome (custom titlebar, min/max/close, drag
// region), native webview zoom, and the updater.
//
// This deliberately answers "is this the desktop app?", NOT "is this a big screen?". The user-agent half
// alone is a trap: a laptop browser viewing the remotely-served UI passes it, then every Tauri call
// behind it throws into a catch and the feature silently does nothing (that is how UI zoom and the
// titlebar both broke). Remote clients are browsers, whatever size they are.
import { isRemote } from "./rpc";

export const isDesktop =
  !/android|iphone|ipad|ipod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "") &&
  !isRemote();

/**
 * The desktop app running on Linux.
 *
 * Only for things the platform genuinely differs on — today, that an undecorated window gets no resize
 * border from the window manager (see WindowResizeFrame). Read from the user agent rather than
 * `@tauri-apps/plugin-os` so it stays a plain constant: `platform()` throws outside Tauri, and this is
 * evaluated at module load in a file that the remote browser build imports too.
 */
export const isLinuxDesktop =
  isDesktop && /linux|x11/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "") &&
  !/android/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "");

/** The phone breakpoint, shared with `mobile.css`. Both must use the same number or JS and CSS disagree
 *  about what a phone is — which shows up as a layout styled for one view running the logic of another. */
export const PHONE_MAX_WIDTH = 767;

/** True on a phone-width viewport. A live check rather than a constant: unlike `isDesktop` this can
 *  change after load (rotation, a resized window), so callers read it when they need it. */
export const isPhoneWidth = (): boolean =>
  typeof window !== "undefined" && window.matchMedia(`(max-width: ${PHONE_MAX_WIDTH}px)`).matches;

/** `isPhoneWidth()` as a hook, re-rendering when the viewport crosses the breakpoint.
 *
 *  The plain function is read during render, so a component only picks up a change if something else
 *  happens to re-render it — which means a rotation can leave a phone showing the desktop treatment (or
 *  the reverse) until the next unrelated state change. Anything that CHOOSES a layout from the width
 *  should use this instead. */
/** Subscribe a component to a media query. `useSyncExternalStore` rather than an effect + state, so the
 *  first render already has the right answer: with state the app painted the desktop layout for a frame
 *  and then swapped. `() => false` is the server/prerender answer — assume desktop. */
function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}
export function usePhoneWidth(): boolean {
  return useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px)`);
}

/*
 * "Is this a touchscreen?" — the `(hover: none)` media query, OVERRULED by an actual mouse.
 *
 * WebKitGTK reports `hover: none` on some Linux desktops (certain Wayland/VM setups) with a mouse plugged
 * in, which put the whole desktop app into its touch layout: message actions inside the bubble behind a
 * "…", enlarged controls. A mouse moving is proof no media query can argue with, so the first
 * `pointermove` from one stamps `data-mouse` on <html>. Every `@media (hover: none)` rule in App.css and
 * mobile.css is scoped to `:where(:root:not([data-mouse]))` (zero specificity — on real touch nothing
 * changes), and JS asks `isTouchOnly()`, so both flip together. A real touchscreen only ever sends
 * pointerType "touch"/"pen", so it never gets the attribute.
 */
const MOUSE_EVENT = "rin:mouse-seen";
/** Remembered across runs so the next launch starts in the right layout — index.html reads it before the
 *  first paint. Cleared by a real touch on a no-hover device, so an iPad that once had a trackpad doesn't
 *  stay stuck with hover-only controls it can't reveal. */
const MOUSE_SEEN_LS = "rin_mouse_seen"; // localStorage, not a vault setting (hence not *KEY: settingsAcl.test scans those)
if (typeof window !== "undefined") {
  const root = document.documentElement;
  const set = (on: boolean) => {
    if (root.hasAttribute("data-mouse") === on) return;
    if (on) root.setAttribute("data-mouse", ""); else root.removeAttribute("data-mouse");
    try { if (on) localStorage.setItem(MOUSE_SEEN_LS, "1"); else localStorage.removeItem(MOUSE_SEEN_LS); } catch { /* private mode */ }
    window.dispatchEvent(new Event(MOUSE_EVENT));
  };
  window.addEventListener("pointermove", (e) => { if (e.pointerType === "mouse") set(true); }, { passive: true });
  window.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "touch" && window.matchMedia("(hover: none)").matches) set(false);
  }, { passive: true });
}

/** No hover to reveal things with — a touchscreen. Use this, never `matchMedia("(hover: none)")` directly. */
export function isTouchOnly(): boolean {
  return window.matchMedia("(hover: none)").matches && !document.documentElement.hasAttribute("data-mouse");
}

/** `isTouchOnly()` as a hook, re-rendering when either half changes.
 *
 *  Prefer this over `usePhoneWidth()` when the question is "can the user reveal this by hovering?" rather
 *  than "is there room for it?". Getting the two mixed up leaves controls that CSS hides but layout still
 *  reserves space for. */
export function useHoverNone(): boolean {
  const subscribe = (cb: () => void) => {
    const mq = window.matchMedia("(hover: none)");
    mq.addEventListener("change", cb);
    window.addEventListener(MOUSE_EVENT, cb);
    return () => { mq.removeEventListener("change", cb); window.removeEventListener(MOUSE_EVENT, cb); };
  };
  return useSyncExternalStore(subscribe, isTouchOnly, () => false);
}


/** True when the viewport is short on space for CHROME — a narrow phone, or a phone held sideways.
 *
 *  A landscape phone is wide and short (812x376), so a width query calls it a desktop while vertical
 *  space is scarcer than in portrait. Anything that trims chrome to make room for content should ask
 *  this rather than `usePhoneWidth()`; anything making a LAYOUT decision (columns, stacking) should not,
 *  because 812px really does have room for two columns. */
export function useCompactChrome(): boolean {
  return useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px), (max-height: 500px)`);
}
